import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';

/** Ikona letadla ve zmenšenině legendy — stejný piktogram jako v boxech dopravy. */
function PlaneGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className="text-phx-text-muted shrink-0"
    >
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
    </svg>
  );
}

function Sample({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        'rounded-phx inline-flex h-4 items-center gap-[3px] border px-1 text-[9.5px] font-semibold tabular-nums',
        className
      )}
    >
      {children}
    </span>
  );
}

function Item({ sample, children }: { sample: ReactNode; children: ReactNode }) {
  return (
    <span className="gap-phx-3_5 flex items-center">
      {sample}
      {children}
    </span>
  );
}

/** Statická legenda pod kalendářem — vysvětluje stavy boxů dopravy. */
export function CalendarLegend() {
  const { t } = useTranslation('venua-planner');

  return (
    <div className="border-phx-border bg-phx-surface-2 text-phx-text-2 rounded-b-phx-md px-phx-4_5 py-phx-3 text-phx-xs flex shrink-0 flex-wrap items-center gap-[14px] border-t whitespace-nowrap">
      <Item
        sample={
          <Sample className="border-phx-border-2 bg-phx-surface text-phx-text border-solid">
            ABC
            <PlaneGlyph size={10} />
            DEF
          </Sample>
        }
      >
        {t('calendar.legend.primary')}
      </Item>

      <Item
        sample={
          <span className="mr-phx-3 relative inline-flex">
            <Sample className="border-phx-border-2 bg-phx-surface text-phx-text-2 h-3.5 gap-0.5 border-solid px-[3px] text-[8.5px]">
              WAW
              <PlaneGlyph size={9} />
              KTW
            </Sample>
            <span className="border-phx-border-2 bg-phx-surface absolute top-0.5 -right-1.5 box-border h-2.5 w-[7px] rounded-r-[5px] border border-l-0 border-solid" />
          </span>
        }
      >
        {t('calendar.legend.feeder')}
      </Item>

      <Item
        sample={
          <Sample className="border-phx-border-3 bg-phx-surface text-phx-text-2 border-dashed">
            hh:mm
          </Sample>
        }
      >
        {t('calendar.legend.unknownTime')}
      </Item>

      <span className="text-phx-draft gap-phx-3_5 flex items-center">
        <Sample className="border-phx-draft bg-phx-draft-tint text-phx-draft border-dashed">
          ?–VIE
        </Sample>
        {t('calendar.legend.noLine')}
      </span>

      <span className="text-phx-danger gap-phx-3_5 flex items-center">
        <Sample className="border-phx-danger bg-phx-danger-tint text-phx-danger border-dashed">
          {t('calendar.bar.missingLeg')}
        </Sample>
        {t('calendar.legend.missingLeg')}
      </span>

      <Item
        sample={
          <Sample className="border-phx-border-2 bg-phx-surface text-phx-text-muted border-solid">
            hh:mm −1d
          </Sample>
        }
      >
        {t('calendar.legend.dayBefore')}
      </Item>
    </div>
  );
}
